import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import {
  Paper,
  Group,
  Text,
  ActionIcon,
  Stack,
  Box,
  Loader,
  Center,
  Badge,
  Anchor,
  Title,
  TextInput,
  Button,
  FileInput,
  Modal,
  Alert,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconPhoto,
  IconVideo,
  IconMicrophone,
  IconMessageCircle2,
  IconSend,
  IconUpload,
  IconAlertCircle,
  IconPlayerRecord,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import RecordPitchModal from "../Video/RecordPitchModal";
import { handleAuthError, handleGenericError } from "../../utils/errorHandler";

const PitchReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const { showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [repName, setRepName] = useState("");
  const [repUserId, setRepUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentReviewUser, setCurrentReviewUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [videoUploadNotes, setVideoUploadNotes] = useState("");
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadError, setVideoUploadError] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(new Set());
  const [failedMessages, setFailedMessages] = useState(new Set());
  const [showRecordModal, setShowRecordModal] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervals = useRef(new Map());

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  };

  const pollForVideoUrl = async (messageId, attemptCount = 0) => {
    if (attemptCount >= 15) {
      // Stop polling after 15 attempts and mark as failed
      setLoadingMessages(prev => {
        const updated = new Set(prev);
        updated.delete(messageId);
        return updated;
      });
      setFailedMessages(prev => new Set(prev).add(messageId));
      return;
    }

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v1/pitch-review/message/${messageId}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch message");
      }

      const result = await response.json();
      const messageData = result.data;

      if (messageData.response_includes_video && messageData.response_url) {
        // Video URL is available, update the message and stop polling
        setMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? { ...msg, response_url: messageData.response_url }
              : msg
          )
        );
        setLoadingMessages(prev => {
          const updated = new Set(prev);
          updated.delete(messageId);
          return updated;
        });

        // Clear the polling interval
        if (pollIntervals.current.has(messageId)) {
          clearTimeout(pollIntervals.current.get(messageId));
          pollIntervals.current.delete(messageId);
        }
      } else {
        // Video URL not available yet, schedule next poll
        const timeoutId = setTimeout(() => {
          pollForVideoUrl(messageId, attemptCount + 1);
        }, 2000);

        pollIntervals.current.set(messageId, timeoutId);
      }
    } catch (error) {
      // Schedule retry on error
      const timeoutId = setTimeout(() => {
        pollForVideoUrl(messageId, attemptCount + 1);
      }, 2000);

      pollIntervals.current.set(messageId, timeoutId);
    }
  };

  const startPollingForMessage = message => {
    if (message.response_includes_video && !message.response_url) {
      // Check if message is more than 5 minutes old
      const messageTime = new Date(message.created_at);
      const currentTime = new Date();
      const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutes in milliseconds

      if (currentTime - messageTime > fiveMinutesInMs) {
        // Message is more than 5 minutes old, don't poll and mark as failed
        setFailedMessages(prev => new Set(prev).add(message.id));
        return;
      }

      setLoadingMessages(prev => new Set(prev).add(message.id));
      pollForVideoUrl(message.id);
    }
  };

  // Cleanup polling intervals on unmount
  useEffect(() => {
    const intervals = pollIntervals.current;
    return () => {
      intervals.forEach(timeoutId => clearTimeout(timeoutId));
      intervals.clear();
    };
  }, []);

  const sendMessage = async () => {
    if (!newMessage.trim() || sendingMessage) return;

    try {
      setSendingMessage(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        handleAuthError("Invalid jwt");
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/pitch-review/message"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          channel_id: id,
          response_text: newMessage.trim(),
        }),
      });

      if (!response.ok) {
        showError("Failed to send message");
        return;
      }

      const result = await response.json();
      const newMessageData = result.data;

      // Add the new message to the messages list
      setMessages(prev => [...prev, newMessageData]);
      setNewMessage("");

      // Scroll to bottom to show the new message
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      handleGenericError(error, "Failed to send message. Please try again.");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    sendMessage();
  };

  const handleKeyPress = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleVideoUpload = async () => {
    if (!selectedVideoFile) {
      setVideoUploadError("Please select a video file first.");
      return;
    }

    setUploadingVideo(true);
    setVideoUploadError("");

    try {
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setVideoUploadError("Authentication failed");
        return;
      }

      // Create file name
      const fileName = `video-message-${Date.now()}.${selectedVideoFile.name.split(".").pop()}`;

      // Step 1: Get presigned URL for video upload
      const apiResponse = await fetch(
        getApiEndpoint("/v1/pitch-review/video/upload"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            channel_id: id,
            video_file_name: fileName,
            video_file_content_type: selectedVideoFile.type,
            response_text: videoUploadNotes.trim(),
          }),
        }
      );

      if (!apiResponse.ok) {
        const errorData = await apiResponse.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to get upload URL");
      }

      const result = await apiResponse.json();
      const responseData = result.data;
      const presignedUrl =
        responseData.presign?.uploadUrl || responseData.uploadUrl;

      if (!presignedUrl) {
        throw new Error("No presigned URL received from server");
      }

      // Step 2: Upload video to presigned URL
      const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        body: selectedVideoFile,
        headers: {
          "Content-Type": selectedVideoFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      // Add the new message to the messages list
      const newMessageData = responseData.message || result.data;
      if (newMessageData) {
        setMessages(prev => [...prev, newMessageData]);

        // Start polling for video URL if the message includes video but no URL yet
        startPollingForMessage(newMessageData);
      }

      // Close modal and reset state
      handleCloseVideoModal();

      // Scroll to bottom to show the new message
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      setVideoUploadError(
        error.message || "Failed to upload video. Please try again."
      );
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
    setSelectedVideoFile(null);
    setVideoUploadNotes("");
    setVideoUploadError("");
    setUploadingVideo(false);
  };

  const handleOpenVideoModal = () => {
    setShowVideoModal(true);
    setVideoUploadError("");
  };

  const handleOpenRecordModal = () => {
    setShowRecordModal(true);
  };

  const handleCloseRecordModal = () => {
    setShowRecordModal(false);
  };

  const handleRecordSubmit = async result => {
    // The RecordPitchModal calls onSubmit after it completes its own upload
    // Since it uses the wrong channel ID, we need to refresh messages to see if anything was added
    if (result?.success) {
      // Refresh messages to show the newly recorded video
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;

        const response = await fetch(
          getApiEndpoint(`/v1/pitch-review/channel/${id}`),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          const data = result.data;
          setMessages(data.messages || []);

          // Check for newly added messages that need polling
          (data.messages || []).forEach(message => {
            startPollingForMessage(message);
          });

          // Scroll to bottom to show the new message
          setTimeout(scrollToBottom, 100);
        }
      } catch (error) {}
    }
  };

  // Load pitch review data
  useEffect(() => {
    const loadPitchReviewData = async () => {
      if (!id) {
        showError("Invalid pitch review ID");
        navigate("/dashboard");
        return;
      }

      try {
        setLoading(true);
        const jwt = await getVerifiedJWT();

        if (!jwt) {
          handleAuthError("Invalid jwt");
          navigate("/login");
          return;
        }

        const response = await fetch(
          getApiEndpoint(`/v1/pitch-review/channel/${id}`),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            showError("Pitch review not found");
          } else if (response.status === 403) {
            showError("You don't have access to this pitch review");
          } else {
            showError("Failed to load pitch review");
          }
          navigate("/dashboard");
          return;
        }

        const result = await response.json();
        const data = result.data;

        setRepName(data.rep_name || "Unknown Rep");
        setRepUserId(data.rep_user_id);
        setMessages(data.messages || []);
        setCurrentReviewUser(data.current_review_user);

        // Check existing messages for video polling
        (data.messages || []).forEach(message => {
          startPollingForMessage(message);
        });

        // Scroll to bottom after messages are loaded
        setTimeout(scrollToBottom, 100);
      } catch (error) {
        handleGenericError(
          error,
          "Failed to load pitch review. Please try again."
        );
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };

    loadPitchReviewData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, getVerifiedJWT, showError, navigate]);

  const formatTime = timestamp => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "";

    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDate = timestamp => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "";

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    if (messageDate.getTime() === today.getTime()) {
      return "Today";
    } else if (
      messageDate.getTime() ===
      today.getTime() - 24 * 60 * 60 * 1000
    ) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      });
    }
  };

  const renderMessageContent = message => {
    const hasAttachments =
      message.response_includes_image ||
      message.response_includes_video ||
      message.response_includes_audio;

    return (
      <Stack gap="xs">
        {message.response_text && (
          <Text size="sm">{message.response_text}</Text>
        )}

        {hasAttachments && (
          <Group gap="xs">
            {message.response_includes_image && (
              <Badge
                leftSection={<IconPhoto size={12} />}
                variant="light"
                color="blue"
                size="sm"
              >
                Image
              </Badge>
            )}
            {message.response_includes_video && (
              <Badge
                leftSection={<IconVideo size={12} />}
                variant="light"
                color="green"
                size="sm"
              >
                Video
              </Badge>
            )}
            {message.response_includes_audio && (
              <Badge
                leftSection={<IconMicrophone size={12} />}
                variant="light"
                color="orange"
                size="sm"
              >
                Audio
              </Badge>
            )}
          </Group>
        )}

        {message.response_url && (
          <Anchor href={message.response_url} target="_blank" size="sm">
            View attachment
          </Anchor>
        )}
      </Stack>
    );
  };

  const renderVideoPlayer = (message, isMyMessage) => {
    const isLoading = loadingMessages.has(message.id);
    const isFailed = failedMessages.has(message.id);
    const isRepUser =
      message.response_user_id === repUserId ||
      message.response_user?.id === repUserId;
    const nameColor = isRepUser ? "orange" : "gray";

    return (
      <Group justify={isMyMessage ? "flex-end" : "flex-start"} mb="sm">
        <Stack gap="xs" style={{ maxWidth: "70%" }}>
          {!isMyMessage && (
            <Text size="xs" fw={500} c={nameColor}>
              {message.response_user_name ||
                (message.response_user?.first_name &&
                message.response_user?.last_name
                  ? `${message.response_user.first_name} ${message.response_user.last_name}`
                  : message.response_user?.first_name ||
                    message.response_user?.last_name ||
                    "Unknown User")}
            </Text>
          )}
          {message.video_title && (
            <Text
              size="sm"
              fw={700}
              mb={-5}
              style={{ textAlign: isMyMessage ? "right" : "left" }}
            >
              Video Pitch: {message.video_title}
            </Text>
          )}
          {isFailed ? (
            <Paper
              p="lg"
              radius="sm"
              bg={isMyMessage ? "orange.1" : "gray.0"}
              style={{
                border: "none",
                width: "300px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <IconAlertCircle size={48} color="red" />
              <Text size="sm" c="red" ta="center">
                Failed to send video
              </Text>
            </Paper>
          ) : isLoading ? (
            <Paper
              p="lg"
              radius="sm"
              bg={isMyMessage ? "orange.1" : "gray.0"}
              style={{
                border: "none",
                width: "300px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <Loader size="md" />
              <Text size="sm" c="dimmed" ta="center">
                Processing video...
              </Text>
            </Paper>
          ) : (
            <video
              controls
              style={{ width: "100%", maxWidth: "300px", borderRadius: "8px" }}
              preload="metadata"
            >
              <source src={message.response_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
          <Text
            size="xs"
            c="dimmed"
            style={{ textAlign: isMyMessage ? "right" : "left" }}
          >
            {formatTime(message.created_at)}
          </Text>
        </Stack>
      </Group>
    );
  };

  const renderMessage = (message, index) => {
    const isMyMessage =
      message.response_user_id === currentReviewUser ||
      message.response_user?.id === currentReviewUser;
    const isRepUser =
      message.response_user_id === repUserId ||
      message.response_user?.id === repUserId;
    const nameColor = isRepUser ? "orange" : "gray";
    const showDateDivider =
      index === 0 ||
      formatDate(message.created_at) !==
        formatDate(messages[index - 1]?.created_at);

    // If message has both text and video, split into two messages
    if (message.response_text && message.response_includes_video) {
      return (
        <React.Fragment key={message.id}>
          {showDateDivider && (
            <Center mb="md">
              <Text size="sm" c="dimmed">
                {formatDate(message.created_at)}
              </Text>
            </Center>
          )}
          {/* First message: Text only */}
          <Group justify={isMyMessage ? "flex-end" : "flex-start"} mb="sm">
            <Stack gap="xs" style={{ maxWidth: "70%" }}>
              {!isMyMessage && (
                <Text size="xs" fw={500} c={nameColor}>
                  {message.response_user_name ||
                    (message.response_user?.first_name &&
                    message.response_user?.last_name
                      ? `${message.response_user.first_name} ${message.response_user.last_name}`
                      : message.response_user?.first_name ||
                        message.response_user?.last_name ||
                        "Unknown User")}
                </Text>
              )}
              <Paper
                p="xs"
                radius="sm"
                bg={isMyMessage ? "orange.1" : "gray.0"}
                style={{ border: "none" }}
              >
                <Text size="sm">{message.response_text}</Text>
              </Paper>
              <Text
                size="xs"
                c="dimmed"
                style={{ textAlign: isMyMessage ? "right" : "left" }}
              >
                {formatTime(message.created_at)}
              </Text>
            </Stack>
          </Group>
          {/* Second message: Video player */}
          {renderVideoPlayer(message, isMyMessage)}
        </React.Fragment>
      );
    }

    // Original message rendering for non-video or video-only messages
    return (
      <React.Fragment key={message.id}>
        {showDateDivider && (
          <Center mb="md">
            <Text size="sm" c="dimmed">
              {formatDate(message.created_at)}
            </Text>
          </Center>
        )}
        {/* For video-only messages, show video player without border */}
        {message.response_includes_video && !message.response_text ? (
          renderVideoPlayer(message, isMyMessage)
        ) : (
          /* Regular messages with timestamp outside */
          <Group justify={isMyMessage ? "flex-end" : "flex-start"} mb="sm">
            <Stack gap="xs" style={{ maxWidth: "70%" }}>
              {!isMyMessage && (
                <Text size="xs" fw={500} c={nameColor}>
                  {message.response_user_name ||
                    (message.response_user?.first_name &&
                    message.response_user?.last_name
                      ? `${message.response_user.first_name} ${message.response_user.last_name}`
                      : message.response_user?.first_name ||
                        message.response_user?.last_name ||
                        "Unknown User")}
                </Text>
              )}
              <Paper
                p="xs"
                radius="xl"
                bg={isMyMessage ? "orange.1" : "gray.0"}
                style={{ border: "none" }}
              >
                {renderMessageContent(message)}
              </Paper>
              <Text
                size="xs"
                c="dimmed"
                style={{ textAlign: isMyMessage ? "right" : "left" }}
              >
                {formatTime(message.created_at)}
              </Text>
            </Stack>
          </Group>
        )}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <PageContent>
        <Paper withBorder p="lg">
          <Group mb="lg">
            <ActionIcon variant="subtle" onClick={() => navigate(-1)} size="lg">
              <IconChevronLeft size={20} />
            </ActionIcon>
            <Title order={2}>Loading...</Title>
          </Group>
          <Center py="xl">
            <Stack align="center" gap="md">
              <Loader size="lg" />
              <Text>Loading pitch review...</Text>
            </Stack>
          </Center>
        </Paper>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <Paper withBorder>
        {/* Header */}
        <Group
          p="md"
          style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}
        >
          <ActionIcon variant="subtle" onClick={() => navigate(-1)} size="lg">
            <IconChevronLeft size={20} />
          </ActionIcon>
          <Stack gap={2}>
            <Title order={2}>Pitch Review</Title>
            <Text size="sm" c="dimmed">
              {repName}
            </Text>
          </Stack>
        </Group>

        {/* Messages Container */}
        <Box
          p="md"
          style={{ height: "calc(100vh - 280px)", overflowY: "auto" }}
        >
          {messages.length === 0 ? (
            <Center py="xl">
              <Stack align="center" gap="md">
                <IconMessageCircle2 size={48} color="gray" />
                <Title order={3}>No messages yet</Title>
                <Text c="dimmed" ta="center">
                  Messages will appear here once the pitch review begins.
                </Text>
              </Stack>
            </Center>
          ) : (
            <Stack gap="xs">
              {messages.map((message, index) => renderMessage(message, index))}
              <div ref={messagesEndRef} />
            </Stack>
          )}
        </Box>

        {/* Message Input Section */}
        <Box
          p="md"
          style={{
            borderTop: "1px solid var(--mantine-color-gray-3)",
            backgroundColor: "var(--mantine-color-gray-0)",
          }}
        >
          <form onSubmit={handleSubmit}>
            <Group gap="sm">
              <TextInput
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                style={{ flex: 1 }}
                disabled={sendingMessage}
              />
              <ActionIcon
                variant="light"
                color="red"
                size="lg"
                onClick={handleOpenRecordModal}
                disabled={sendingMessage || uploadingVideo}
                title="Record video"
              >
                <IconPlayerRecord size={20} />
              </ActionIcon>
              <ActionIcon
                variant="light"
                color="blue"
                size="lg"
                onClick={handleOpenVideoModal}
                disabled={sendingMessage || uploadingVideo}
                title="Upload video"
              >
                <IconVideo size={20} />
              </ActionIcon>
              <Button
                type="submit"
                loading={sendingMessage}
                disabled={!newMessage.trim() || sendingMessage}
                leftSection={<IconSend size={16} />}
              >
                Send
              </Button>
            </Group>
          </form>
        </Box>
      </Paper>

      {/* Video Upload Modal */}
      <Modal
        opened={showVideoModal}
        onClose={handleCloseVideoModal}
        title="Upload Video Message"
        size="md"
        padding="xl"
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            Select a video file to share in the pitch review conversation.
          </Text>

          {videoUploadError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="red"
              variant="light"
            >
              {videoUploadError}
            </Alert>
          )}

          <FileInput
            label="Select Video File"
            placeholder="Click to select a video file"
            value={selectedVideoFile}
            onChange={setSelectedVideoFile}
            accept="video/*"
            leftSection={<IconUpload size={16} />}
            disabled={uploadingVideo}
          />

          <TextInput
            label="Message (Optional)"
            placeholder="Add a note with your video..."
            value={videoUploadNotes}
            onChange={e => setVideoUploadNotes(e.target.value)}
            disabled={uploadingVideo}
          />

          <Group justify="flex-end" gap="sm">
            <Button
              variant="outline"
              onClick={handleCloseVideoModal}
              disabled={uploadingVideo}
            >
              Cancel
            </Button>
            <Button
              onClick={handleVideoUpload}
              disabled={!selectedVideoFile}
              loading={uploadingVideo}
              leftSection={<IconUpload size={16} />}
            >
              Upload Video
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Record Video Modal */}
      <RecordPitchModal
        opened={showRecordModal}
        onClose={handleCloseRecordModal}
        onSubmit={handleRecordSubmit}
        videoTitle="Pitch Review Video"
        videoId={null}
        courseId={null}
        initialChannelId={id}
        onSubmitSuccess={() => {}}
      />
    </PageContent>
  );
};

export default PitchReviewPage;
